import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { MultiSelect } from '@/components/ui/multi-select';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { User, UserMinus, Trash2, Plus } from 'lucide-react';
import { Control, FieldPath } from 'react-hook-form';

// Type definitions for different form schemas
interface BasicTeamMember {
    id?: string;
    fullName?: string;
    email?: string;
    roleIds: string[];
}

// Props for different modes
interface TeamMemberFormCardProps {
    mode: 'simple' | 'detailed';
    index: number;
    fieldId: string;
    canRemove: boolean;
    onRemove: () => void;

    // For simple mode (Step2TeamInformation)
    simpleProps?: {
        control: Control<{ teamMembers: BasicTeamMember[] }>;
        roleOptions: Array<{ value: string; label: string }>;
        isLoadingRoles?: boolean;
    };

    // For detailed mode (AddUsersStep)
    detailedProps?: {
        register: any;
        setValue: any;
        watch: any;
        errors: any;
        roleOptions: string[];
        departmentOptions: string[];
        permissionOptions: string[];
        selectedDepartments: string[];
        onPermissionChange: (permission: string, checked: boolean) => void;
    };
}

export function TeamMemberFormCard({
    mode,
    index,
    fieldId,
    canRemove,
    onRemove,
    simpleProps,
    detailedProps
}: TeamMemberFormCardProps) {
    if (mode === 'simple' && simpleProps) {
        return (
            <Card key={fieldId} className="border border-border">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5 text-muted-foreground" />
                            <h3 className="text-md font-medium text-foreground">
                                Member {index + 1}
                            </h3>
                        </div>
                        {canRemove && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={onRemove}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 flex items-center gap-1 border border-red-200"
                            >
                                <UserMinus className="w-4 h-4" />
                                Remove
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Full Name */}
                        <FormField
                            control={simpleProps.control}
                            name={`teamMembers.${index}.fullName` as FieldPath<{ teamMembers: BasicTeamMember[] }>}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-foreground font-medium">
                                        Full Name
                                        <span className="text-red-600">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="Enter full name"
                                            value={(field.value as string) || ''}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            name={field.name}
                                            className="mt-1"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Email */}
                        <FormField
                            control={simpleProps.control}
                            name={`teamMembers.${index}.email` as FieldPath<{ teamMembers: BasicTeamMember[] }>}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-foreground font-medium">
                                        Email<span className="text-red-600">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="email"
                                            placeholder="Enter email address"
                                            value={(field.value as string) || ''}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            name={field.name}
                                            className="mt-1"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Role */}
                        <FormField
                            control={simpleProps.control}
                            name={`teamMembers.${index}.roleIds` as FieldPath<{ teamMembers: BasicTeamMember[] }>}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-foreground font-medium">
                                        Role in Your Company
                                        <span className="text-red-600">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <MultiSelect
                                            options={simpleProps.roleOptions}
                                            selected={(field.value as string[]) || []}
                                            onChange={field.onChange}
                                            placeholder="Select roles..."
                                            disabled={simpleProps.isLoadingRoles}
                                            className="mt-1"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (mode === 'detailed' && detailedProps) {
        const { register, setValue, watch, errors, roleOptions, permissionOptions, selectedDepartments, onPermissionChange } = detailedProps;

        return (
            <Card key={fieldId} className="border border-border mb-4">
                <CardHeader className="pb-3">
                    <div className="flex justify-between items-center">
                        <h3 className="text-sm font-medium">Team Member {index + 1}</h3>
                        <Button
                            type="button"
                            onClick={onRemove}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">First Name *</label>
                            <Input
                                {...register(`teamMembers.${index}.firstName` as const)}
                                placeholder="Enter first name"
                            />
                            {errors.teamMembers?.[index]?.firstName && (
                                <p className="text-sm text-red-600 mt-1">
                                    {errors.teamMembers[index]?.firstName?.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">Last Name *</label>
                            <Input
                                {...register(`teamMembers.${index}.lastName` as const)}
                                placeholder="Enter last name"
                            />
                            {errors.teamMembers?.[index]?.lastName && (
                                <p className="text-sm text-red-600 mt-1">
                                    {errors.teamMembers[index]?.lastName?.message}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Email Address *</label>
                            <Input
                                {...register(`teamMembers.${index}.email` as const)}
                                type="email"
                                placeholder="Enter email address"
                            />
                            {errors.teamMembers?.[index]?.email && (
                                <p className="text-sm text-red-600 mt-1">
                                    {errors.teamMembers[index]?.email?.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">Phone Number</label>
                            <Input
                                {...register(`teamMembers.${index}.phoneNumber` as const)}
                                placeholder="(555) 123-4567"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Role *</label>
                            <Select onValueChange={(value) => setValue(`teamMembers.${index}.role`, value)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                                <SelectContent>
                                    {roleOptions.map((role) => (
                                        <SelectItem key={role} value={role}>
                                            {role}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.teamMembers?.[index]?.role && (
                                <p className="text-sm text-red-600 mt-1">
                                    {errors.teamMembers[index]?.role?.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">Department</label>
                            <Select onValueChange={(value) => setValue(`teamMembers.${index}.department`, value)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select department" />
                                </SelectTrigger>
                                <SelectContent>
                                    {selectedDepartments.map((dept) => (
                                        <SelectItem key={dept} value={dept}>
                                            {dept}
                                        </SelectItem>
                                    ))}
                                    {selectedDepartments.length === 0 && (
                                        <SelectItem value="none" disabled>
                                            Select departments above first
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Permissions */}
                    <div>
                        <label className="block text-sm font-medium mb-3">Permissions</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {permissionOptions.map((permission) => {
                                const currentPermissions = watch(`teamMembers.${index}.permissions`) || [];
                                return (
                                    <div key={permission} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`${index}-${permission}`}
                                            checked={currentPermissions.includes(permission)}
                                            onCheckedChange={(checked) => onPermissionChange(permission, checked as boolean)}
                                        />
                                        <label
                                            htmlFor={`${index}-${permission}`}
                                            className="text-xs font-medium"
                                        >
                                            {permission}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return null;
}

// Container component for team member cards with add button
interface TeamMemberFormContainerProps {
    mode: 'simple' | 'detailed';
    children: React.ReactNode;
    onAddMember: () => void;
    addButtonText?: string;
    addButtonStyle?: 'center' | 'right';
}

export function TeamMemberFormContainer({
    mode,
    children,
    onAddMember,
    addButtonText = "Add Another Team Member",
    addButtonStyle = "center"
}: TeamMemberFormContainerProps) {
    const buttonClass = mode === 'simple'
        ? "w-1/3 h-12 bg-foreground-soft hover:bg-foreground text-primary-foreground flex items-center justify-center gap-2 transition-colors"
        : "flex items-center gap-2";

    const containerClass = addButtonStyle === 'center' ? "flex justify-center" : "flex justify-end";

    return (
        <div className="space-y-4">
            {children}
            <div className={containerClass}>
                <Button
                    type="button"
                    onClick={onAddMember}
                    className={buttonClass}
                    variant={mode === 'simple' ? 'default' : 'outline'}
                    size={mode === 'simple' ? 'default' : 'sm'}
                >
                    <Plus className="w-4 h-4" />
                    {addButtonText}
                </Button>
            </div>
        </div>
    );
}

// Empty state component
interface TeamMemberEmptyStateProps {
    onAddFirst: () => void;
    message?: string;
    buttonText?: string;
}

export function TeamMemberEmptyState({
    onAddFirst,
    message = "No team members added yet",
    buttonText = "Add Your First Team Member"
}: TeamMemberEmptyStateProps) {
    return (
        <Card className="border border-dashed border-border">
            <CardContent className="pt-6 text-center">
                <User className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-3">{message}</p>
                <Button
                    type="button"
                    onClick={onAddFirst}
                    variant="outline"
                    size="sm"
                >
                    {buttonText}
                </Button>
            </CardContent>
        </Card>
    );
}
